import { countryName } from '@clockwise/shared';

/**
 * Renders a postal address as stacked lines, collapsing empty fields and
 * joining locality/region/postal onto one line. Country is rendered through
 * `countryName` (stored as an ISO-3166 alpha-2 code; falls back to the raw
 * value for legacy free-text rows). Empty input renders an em dash.
 *
 * Callers normalize their own field names onto this shape — the customer
 * `address` object uses street/city/state, customer contacts use
 * address1/address2/locality/region — so the line-building logic lives in one
 * place instead of being copied per screen.
 */
export interface AddressLinesProps {
  line1?: string | null;
  line2?: string | null;
  locality?: string | null;
  region?: string | null;
  postalCode?: string | null;
  country?: string | null;
}

export function AddressLines({
  line1,
  line2,
  locality,
  region,
  postalCode,
  country,
}: AddressLinesProps) {
  const lines = [
    line1,
    line2,
    [locality, region, postalCode].filter(Boolean).join(', '),
    country ? countryName(country) : null,
  ].filter(Boolean) as string[];

  if (lines.length === 0) return <span className="text-muted-foreground">—</span>;
  return (
    <div className="text-sm">
      {lines.map((l, i) => (
        <div key={`${l}-${i}`}>{l}</div>
      ))}
    </div>
  );
}
